import React, { useEffect, useMemo, useRef, useState } from 'react';

// Tilted wall of looping work clips behind a dark hero — same system as the
// Loop Foundry landing page: columns drift up/down in perspective and the
// wall leans toward the pointer. Clips come from scripts/build-hero-wall.mjs.
// A dark wash in the hero's background color keeps the headline readable;
// in dark mode the wall also fades into the page at the bottom.
// A mode without clips yet (prototyping) renders labelled placeholder tiles.

const MANIFEST_URL = '/assets/hero-wall/manifest.json';
const PLACEHOLDER_COUNT = 8;
const GAP = 20;
// Wall overscan, matches .hero-wall__grid's inset (-35% / -20%).
const OVERSCAN_X = 1.4;
const OVERSCAN_Y = 1.7;
// Loop Foundry's drift speeds: column c moves 2343px in (70 + 9c)s.
const speedFor = (c) => 2343 / (70 + c * 9);

let manifestPromise;
const loadManifest = () => {
  manifestPromise ??= fetch(MANIFEST_URL).then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
  return manifestPromise;
};

// Seeded shuffle: a fresh seed per page load gives a new order on every
// reload, while resizes and re-renders keep the order they started with.
const seeded = (items, seed) => {
  const a = [...items];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// `still` renders posters only, fully shown and motionless (OG card renders).
const HeroVideoWall = ({ mode, tint, ink = '#ffffff', tintOpacity = 0.78, dense = false, still = false }) => {
  const rootRef = useRef(null);
  const wallRef = useRef(null);
  const [media, setMedia] = useState(null);
  const [size, setSize] = useState(null);
  const [seed] = useState(() => Math.floor(Math.random() * 233280));
  const [prefersReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const reduce = still || prefersReduced;

  useEffect(() => {
    let alive = true;
    loadManifest().then((m) => alive && setMedia(m[mode] || []));
    return () => { alive = false; };
  }, [mode]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      setSize((s) => (s && Math.abs(s.w - width) < 40 && Math.abs(s.h - height) < 40 ? s : { w: width, h: height }));
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  const hasClips = media?.length > 0;
  // Few, big columns: every column gets its own clips (dealt round-robin),
  // so a clip only ever repeats inside its own column, one full set later —
  // never twice on screen at once. `dense` is Loop Foundry's own small-tile
  // grid, for short portrait loops.
  const isMobile = size && size.w < 768;
  const columns = dense ? (isMobile ? 3 : 6) : (isMobile ? 2 : 3);

  const grid = useMemo(() => {
    if (!media || !size) return [];
    const pool = hasClips
      ? seeded(media, seed)
      : Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => ({ placeholder: i + 1, w: 16, h: 9 }));
    const colW = (size.w * OVERSCAN_X - GAP * (columns - 1)) / columns;
    const need = size.h * OVERSCAN_Y + colW; // + one tile of slack for the tilt
    return Array.from({ length: columns }, (_, c) => {
      const set = pool.filter((_, i) => i % columns === c);
      const shift = set.reduce((sum, m) => sum + (colW * m.h) / m.w + GAP, 0);
      // Enough copies that the column still covers the wall after drifting
      // one full set, so no gap ever scrolls into view.
      const copies = Math.ceil(need / shift) + 1;
      return { items: Array.from({ length: copies }, () => set).flat(), shift, duration: shift / speedFor(c) };
    });
  }, [media, size, seed, columns, hasClips]);

  // Tiles fade in as they drift onto the screen and out as they leave.
  // Playback uses a stricter check: a tilted tile's bounding box is much
  // bigger than the tile, so "intersecting" alone also starts clips that sit
  // just off screen. Clips play only while their box reaches into the
  // viewport shrunk by 10% per side. (A minimum ratio doesn't work here: big
  // tilted boxes never reach it, so only the middle column would play.)
  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduce || !grid.length) return;
    const fade = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
        target.classList.toggle('is-in', isIntersecting && intersectionRatio >= 0.1);
      });
    }, { threshold: [0, 0.1] });
    const play = new IntersectionObserver((entries) => {
      entries.forEach(({ target: v, isIntersecting }) => {
        if (isIntersecting) {
          if (!v.src) v.src = v.dataset.src;
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    }, { rootMargin: '-10% -10% -10% -10%' });
    root.querySelectorAll('.hero-wall__tile').forEach((t) => fade.observe(t));
    root.querySelectorAll('video').forEach((v) => play.observe(v));
    return () => { fade.disconnect(); play.disconnect(); };
  }, [grid, reduce]);

  // Pointer lean, eased so it never snaps. Mouse and trackpad only.
  useEffect(() => {
    const wall = wallRef.current;
    if (!wall || reduce || !window.matchMedia('(pointer: fine)').matches) return;
    let tx = 0, ty = 0, x = 0, y = 0, raf = 0;
    const step = () => {
      x += (tx - x) * 0.05; y += (ty - y) * 0.05;
      wall.style.setProperty('--mx', x.toFixed(4));
      wall.style.setProperty('--my', y.toFixed(4));
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.001 ? requestAnimationFrame(step) : 0;
    };
    const go = () => { if (!raf) raf = requestAnimationFrame(step); };
    const onMove = (e) => { tx = (e.clientX / window.innerWidth) * 2 - 1; ty = (e.clientY / window.innerHeight) * 2 - 1; go(); };
    const onLeave = () => { tx = ty = 0; go(); };
    window.addEventListener('pointermove', onMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  }, [reduce]);

  return (
    <div ref={rootRef} className={`hero-wall${reduce ? ' is-still' : ''}`} aria-hidden="true" style={{ backgroundColor: tint }}>
      <div ref={wallRef} className={`hero-wall__grid${hasClips ? '' : ' is-placeholder'}${grid.length ? ' is-ready' : ''}`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: GAP }}>
        {grid.map(({ items, shift, duration }, c) => (
          <div
            key={c}
            className={`hero-wall__col${reduce ? ' is-still' : ''}`}
            style={{ '--d': `${duration.toFixed(1)}s`, '--shift': `${shift.toFixed(1)}px`, gap: GAP, paddingBottom: GAP, animationDirection: c % 2 ? 'reverse' : 'normal' }}
          >
            {items.map((m, k) => (m.placeholder ? (
              <div key={k} className="hero-wall__tile hero-wall__tile--placeholder" style={{ '--ar': `${m.w}/${m.h}`, color: ink }}>
                <span>PROTOTYPE / {String(m.placeholder).padStart(2, '0')}</span>
              </div>
            ) : (
              <div
                key={k}
                className="hero-wall__tile"
                style={{ '--ar': `${m.w}/${m.h}`, backgroundImage: `url("/assets/hero-wall/${mode}/${m.f}.jpg")` }}
              >
                {!reduce && <video muted loop playsInline preload="none" data-src={`/assets/hero-wall/${mode}/${m.f}.mp4`} />}
              </div>
            )))}
          </div>
        ))}
      </div>
      {/* Placeholders are drawn in the ink color already, so they skip the wash. */}
      {hasClips && <div className="absolute inset-0" style={{ backgroundColor: tint, opacity: tintOpacity }} />}
      <div className="hero-wall__fade" />
    </div>
  );
};

export default HeroVideoWall;
